import React, { useEffect, useMemo, useRef, useState } from "react";
import { RowScaler, RowInner, StatCard, Icon, Count, Label } from "./styles";
import { DesktopIcon, ServerIcon, UsersIcon, PlugIcon } from "./icons";

const CountUp = (value, duration = 700) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let raf = 0;
    const start = performance.now();
    const from = 0;
    const to = Math.max(0, value);

    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(from + (to - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);

  return display.toLocaleString();
};

const StatItem = ({ color, label, icon, value }) => {
  const counted = CountUp(value);
  return (
    <StatCard $color={color}>
      <Icon $color={color}>{icon}</Icon>
      <Count>{counted}</Count>
      <Label>{label}</Label>
    </StatCard>
  );
};

const InstanceSummary = ({ data }) => {
  const vdi = data.kinds.vdi;
  const server = data.kinds.server;
  const renderfarm = data.kinds.renderfarm;
  const horizon = data.kinds.horizon;
  const user = data.user;
  const connected = data.connected;

  const stats = useMemo(() => {
    const arr = [
      { icon: <DesktopIcon />, label: "VDI", value: vdi, color: "#6366F1" },
      { icon: <ServerIcon />, label: "서버", value: server, color: "#3B82F6" },
    ];

    if (renderfarm && renderfarm > 0) {
      arr.push({
        icon: <ServerIcon />,
        label: "RENDERFARM",
        value: renderfarm,
        color: "#8B5CF6",
      });
    }

    if (horizon && horizon > 0) {
      arr.push({
        icon: <DesktopIcon />,
        label: "HORIZON",
        value: horizon,
        color: "#06B6D4",
      });
    }

    arr.push(
      { icon: <UsersIcon />, label: "사용자", value: user, color: "#22C55E" },
      {
        icon: <PlugIcon />,
        label: "접속 중",
        value: connected,
        color: "#F59E0B",
      }
    );

    return arr;
  }, [vdi, server, renderfarm, horizon, user, connected]);

  const hostRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);
  const [innerH, setInnerH] = useState<number | undefined>(undefined);

  useEffect(() => {
    const host = hostRef.current;
    const inner = innerRef.current;
    if (!host || !inner) return;

    const measure = () => {
      const w = host.clientWidth;

      host.dataset.density =
        w <= 330
          ? "nano"
          : w <= 380
          ? "micro"
          : w <= 520
          ? "compact"
          : "normal";

      inner.style.transform = "scale(1)";
      const available = host.clientWidth;
      const need = inner.scrollWidth;
      const next = Math.max(0.42, Math.min(1, available / Math.max(1, need)));
      setScale(next);

      const rawH = inner.getBoundingClientRect().height;
      setInnerH(rawH * next);
    };

    const ro = new ResizeObserver(measure);
    ro.observe(host);
    ro.observe(inner);
    measure();
    return () => ro.disconnect();
  }, []);

  return (
    <RowScaler ref={hostRef} data-density="normal" style={{ height: innerH }}>
      <RowInner
        ref={innerRef}
        className="row-inner"
        style={{ transform: `scale(${scale})` }}
      >
        {stats.map((s) => (
          <StatItem key={s.label} {...s} />
        ))}
      </RowInner>
    </RowScaler>
  );
};

export default InstanceSummary;
